import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, InfoCircle, Clock } from '@untitled-ui/icons-react'
import { MyButton, MyLoadingData } from '@interstellar-component'

import AuthImage from '@src/components/AuthImage'
import { useCompany } from '../../Context'
import { BillingService } from '../../billing.service'
import { WalletService, PricingService, GolonganService } from '../../wallet.service'
import { formatRp } from '../../wallet.format'
import { memberStatusOf } from '../MyMemberStatusChip'
import { credentialGate } from '../agreementStatus'
import GolonganBadge from './GolonganBadge'
import PackageCard from './PackageCard'
import SaldoCard from './SaldoCard'
import SaldoHistory from './SaldoHistory'
import PaymentHistory from './PaymentHistory'
import TopupFlow from './TopupFlow'
import EmptyState from './EmptyState'
import { avatarBadgeFor, emptyStateCopyFor } from './statusPresentation'

/**
 * Company avatar. Exactly one overlay, decided by the membership lifecycle:
 * the verified tick once the company is ACTIVE, otherwise the pill that names
 * what it is still waiting on. The tick is not decoration — it asserts that
 * CLIK validated this company, so it must never appear before that is true.
 */
function CompanyAvatar({ company, badge }) {
  // The active-company landing fetches the `billing` payload, which carries
  // company fields (incl. the full_logo_url virtual) at the TOP LEVEL with no
  // company_information nesting; only the `information` payload nests them. Read
  // both shapes so the logo shows on the main page too, not just the profile
  // page (Everest #86eyrt9dz).
  const logo = company?.company_information?.full_logo_url ?? company?.full_logo_url
  const initial = (company?.name || '?').trim().charAt(0).toUpperCase()

  return (
    <div className="relative">
      {logo ? (
        <AuthImage
          src={logo}
          alt=""
          className="size-[72px] rounded-full border-[3px] border-white object-cover shadow-md"
        />
      ) : (
        <div className="flex size-[72px] items-center justify-center rounded-full border-[3px] border-white bg-brand/50 text-xl font-semibold text-brand/700 shadow-md">
          {initial}
        </div>
      )}
      {badge ? (
        <span
          className={`absolute -bottom-2 left-1/2 w-max -translate-x-1/2 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      ) : (
        <span className="absolute -bottom-0.5 -right-0.5 flex size-6 items-center justify-center rounded-full border-2 border-white bg-blue/600 text-white">
          <svg viewBox="0 0 24 24" className="size-3.5" fill="none" aria-hidden="true">
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </div>
  )
}

const BADGE_SHAPE = PropTypes.shape({ label: PropTypes.string, cls: PropTypes.string })

CompanyAvatar.propTypes = { company: PropTypes.object, badge: BADGE_SHAPE }
CompanyAvatar.defaultProps = { company: null, badge: null }

/**
 * "Company information" — the money/commercial home for an ACTIVE company.
 *
 * Replaces the old quota-unit Billing tab as the landing surface. Four reads,
 * all independent, fired together:
 *
 *   wallet        — Rupiah balance, held, validity, free CR quota (SAW-backed)
 *   pricing       — live golongan-resolved package + per-enquiry tarif
 *   golongan      — the UMKM application state machine
 *   subscription  — status + current_period_end for the package card
 *
 * They are NOT collapsed into one endpoint on purpose: wallet and pricing are
 * proxied to SAW, golongan is tenant-local, and a slow or unreachable SAW must
 * not blank out the parts of the page the tenant owns.
 *
 * EVERY company lands here, active or not — the page shell (Back, the
 * "Company information" heading, the Company profile link, the centred identity
 * block) is invariant across all three landing frames in the design, and only
 * the body region swaps. When the company cannot transact yet, an empty state
 * takes the place of the cards (Figma 15564:30150). Only golongan is read in
 * that mode: the UMKM application is open the whole time, while
 * wallet/pricing/subscription have nothing to describe yet.
 *
 * `presentation` lets the caller state WHY the company cannot transact instead
 * of having it inferred from memberStatusOf. A company still in the enrolment
 * wizard has no membership status at all, so inference would fall back to
 * "Menunggu verifikasi CLIK" — a review nobody has requested.
 */
function CompanyInformation({ companyId, company, footer, presentation }) {
  const navigate = useNavigate()
  const { billingRefreshKey } = useCompany()

  const [wallet, setWallet] = useState(null)
  const [pricing, setPricing] = useState(null)
  const [golongan, setGolongan] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [topupOpen, setTopupOpen] = useState(false)
  // The newest unpaid invoice + which invoice the top-up modal is resuming.
  // This page has no invoice table, so a parked payment (top-up VA / platform
  // fee) is otherwise unreachable — the banner is its only way back.
  const [pendingInvoice, setPendingInvoice] = useState(null)
  const [resumeInvoiceId, setResumeInvoiceId] = useState(null)
  // Bumped when a payment settles. The ledger is a separate fetch from the
  // wallet, so reloading the balance alone left the statement one row stale.
  const [ledgerKey, setLedgerKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Backing out of a never-paid subscribe. The multi-company detail slider has
  // a Billing tab with a Cancel button for this; the single-company view (this
  // component) is the only surface a single-company tenant ever sees, and it had
  // no way to un-stick a `pending_payment` subscription — cancelling the Xendit
  // invoice only voids the invoice, the subscription row stays pending forever.
  const [confirmCancelPending, setConfirmCancelPending] = useState(false)
  const [cancelingPending, setCancelingPending] = useState(false)

  // Commercial actions need an ACTIVE membership (CLIK-validated). The BE
  // enforces this too — this only keeps the buttons from lying.
  const status = memberStatusOf(company)
  const canAct = !presentation && status === 'active'
  // `expired` keeps the cards: its wallet holds real money and its package was
  // really bought. It has lost the right to ACT, not its balance.
  const showEmptyState = !!presentation || (!canAct && status !== 'expired')
  // An approved company still waits for CLIK to countersign the membership
  // agreement before it may enter its CLIK credentials.
  const gate = credentialGate(company)
  const badge =
    presentation?.badge || (status === 'active' ? null : avatarBadgeFor(status, gate))
  const emptyCopy = presentation || emptyStateCopyFor(status, gate)
  const companyName = company?.legal_name || company?.name || null

  const unwrap = (res) =>
    res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    // allSettled, not all: a 502 from SAW on the wallet must still leave the
    // golongan pill and package card rendered.
    //
    // In empty-state mode only golongan is asked for. The other three describe
    // money this company cannot have yet, and asking anyway would surface their
    // failures as a "sebagian data gagal dimuat" banner over a page that is
    // showing exactly what it should.
    const [w, p, g, s, inv] = await Promise.allSettled([
      showEmptyState ? Promise.resolve(null) : WalletService.getWallet(companyId),
      showEmptyState ? Promise.resolve(null) : PricingService.resolve(companyId),
      GolonganService.getStatus(companyId),
      showEmptyState ? Promise.resolve(null) : BillingService.getSubscription(companyId),
      showEmptyState
        ? Promise.resolve(null)
        : BillingService.getInvoices(companyId, { page: 1, limit: 1, status: 'unpaid' }),
    ])
    setWallet(w.status === 'fulfilled' ? unwrap(w.value) : null)
    setPricing(p.status === 'fulfilled' ? unwrap(p.value) : null)
    setGolongan(g.status === 'fulfilled' ? unwrap(g.value) : null)
    setSubscription(s.status === 'fulfilled' ? unwrap(s.value) : null)
    // Newest unpaid invoice → the resume banner. A failed fetch just hides the
    // banner; it is deliberately left OUT of the `failed` check below so it
    // never paints the "sebagian data gagal" warning over an otherwise fine page.
    const invRows = inv.status === 'fulfilled' ? unwrap(inv.value)?.data : null
    setPendingInvoice(Array.isArray(invRows) && invRows.length ? invRows[0] : null)

    const failed = [w, p, g, s].find((r) => r.status === 'rejected')
    if (failed) {
      setError(
        failed.reason?.response?.data?.message ||
          failed.reason?.message ||
          'Some data failed to load',
      )
    }
    setLoading(false)
  }, [companyId, showEmptyState])

  // billingRefreshKey bumps when a panel closes / a billing action completes.
  useEffect(() => {
    load()
  }, [load, billingRefreshKey])

  const goToSubscribePage = () =>
    navigate(`/company/${companyId}/subscribe`, { state: { companyName } })

  // A subscribe whose first invoice was never paid (or was cancelled) leaves the
  // subscription parked at `pending_payment`. `immediate: true` is the only mode
  // that applies to it — there is no paid period to honour — and it voids the
  // still-unpaid invoice on the way out (local-billing cancelSubscription).
  const isPendingSub = subscription?.status === 'pending_payment'
  const handleCancelPending = async () => {
    setCancelingPending(true)
    setError(null)
    try {
      await BillingService.cancelSubscription(companyId, { immediate: true, reason: null })
      setConfirmCancelPending(false)
      await load()
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || 'Failed to cancel the subscription',
      )
    } finally {
      setCancelingPending(false)
    }
  }

  // An APPROVED company is the one pre-active state with something to do rather
  // than something to wait for, and the form that does it lives on the Company
  // profile page — so the empty state has to point at it. Every other membership
  // state resolves without the tenant touching anything. Not while CLIK has yet
  // to countersign the agreement, though: the form is locked until then.
  const emptyActions =
    emptyCopy.actions ||
    (status === 'approved' && !gate.blocked ? (
      <MyButton
        color="primary"
        variant="filled"
        size="md"
        type="button"
        onClick={() => navigate('/company/profile')}
      >
        <span className="text-sm-semibold text-white">Enter CLIK credentials</span>
      </MyButton>
    ) : null)

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-white">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col px-6 py-8">
        {/* ── Page header ──────────────────────────────────────────────── */}
        {/* Go to a stable landing surface, NOT navigate(-1). This is a
            single-company tenant's home page and has no meaningful parent; a
            blind history-back lands on whatever preceded it — routinely the
            create-company wizard (/register-company-info) — which is the loop QA
            reported (Everest #z94x8e0mj0). '/' → LandingRedirect resolves to the
            dashboard / first accessible module. */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-4 flex w-fit items-center gap-2 text-sm-semibold text-gray/600 transition-colors hover:text-gray/900"
        >
          <ArrowLeft className="size-5" />
          Back
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray/200 pb-5">
          <h1 className="text-3xl font-semibold text-gray/900">Company information</h1>
          <MyButton
            color="secondary"
            variant="outlined"
            size="md"
            type="button"
            onClick={() => navigate('/company/profile')}
          >
            <span className="flex items-center gap-1.5 text-sm-semibold">
              Company profile
              <InfoCircle className="size-4" />
            </span>
          </MyButton>
        </div>

        {/* ── Identity ─────────────────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-4 border-b border-gray/200 py-8">
          <CompanyAvatar company={company} badge={badge} />
          <h2 className="max-w-[640px] text-center text-2xl font-semibold text-gray/900">
            {company?.name || '—'}
          </h2>
          <GolonganBadge status={golongan} onChanged={load} />
        </div>

        {error ? (
          <div className="mt-6 rounded-lg border border-warning/200 bg-warning/50 px-4 py-3 text-sm-medium text-warning/700">
            {error}
          </div>
        ) : null}

        {/* ── Unpaid-invoice resume banner ─────────────────────────────────
            This page has no invoice table, so without this a parked top-up VA
            (or platform-fee invoice) would be unreachable — Saldo history's
            Pending tab is empty by design. Reopens the top-up modal at the VA
            step for the invoice that is still owed. */}
        {!showEmptyState && pendingInvoice ? (
          <button
            type="button"
            onClick={() => {
              setResumeInvoiceId(pendingInvoice.id)
              setTopupOpen(true)
            }}
            className="mt-6 flex w-full items-center gap-3 rounded-lg border border-warning/300 bg-warning/50 px-4 py-3 text-left transition-colors hover:bg-warning/100"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-warning/100 text-warning/700">
              <Clock className="size-5" />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-sm-semibold text-warning/700">You have an unfinished payment</span>
              <span className="truncate text-xs-regular text-warning/700">
                {pendingInvoice.invoice_number} · {formatRp(pendingInvoice.total_idr)} — tap to continue
              </span>
            </span>
            <span className="ml-auto shrink-0 text-sm-semibold text-warning/700">View payment →</span>
          </button>
        ) : null}

        {/* ── Package + saldo, or the empty state that stands in for them ── */}
        {showEmptyState ? (
          <div className="py-8">
            {/* `variant` has to travel with the copy. Without it EmptyState
                falls back to its 'empty' default, so a failure was painted in
                the same calm grey-and-purple as "nothing here yet" and never
                announced itself via role="alert". */}
            <EmptyState
              title={emptyCopy.title}
              description={emptyCopy.description}
              actions={emptyActions}
              variant={emptyCopy.variant || 'empty'}
            />
          </div>
        ) : null}

        {!showEmptyState && loading && !wallet && !pricing ? (
          <div className="py-16">
            <MyLoadingData />
          </div>
        ) : null}

        {/* ── Cancel-pending confirmation ──────────────────────────────────
            The single-company view's only exit from a stranded `pending_payment`
            subscribe. Mirrors the multi-company Billing tab's inline confirm. */}
        {!showEmptyState && isPendingSub && confirmCancelPending ? (
          <div className="mt-6 rounded-lg border border-warning/300 bg-warning/50 p-4">
            <p className="text-sm-semibold text-warning/700">
              Cancel the unpaid subscription?
            </p>
            <p className="mt-1 text-xs-regular text-warning/700">
              The unpaid invoice for this subscription will be canceled too. You can start a
              new subscription at any time.
            </p>
            <div className="mt-3 flex gap-2">
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                type="button"
                disabled={cancelingPending}
                onClick={() => setConfirmCancelPending(false)}
              >
                <span className="text-sm-semibold">Back</span>
              </MyButton>
              <MyButton
                color="error"
                variant="filled"
                size="sm"
                type="button"
                disabled={cancelingPending}
                onClick={handleCancelPending}
              >
                <span className="text-sm-semibold text-white">
                  {cancelingPending ? 'Canceling…' : 'Yes, cancel'}
                </span>
              </MyButton>
            </div>
          </div>
        ) : null}

        {!showEmptyState && !(loading && !wallet && !pricing) ? (
          <div className="flex flex-col gap-5 py-8 lg:flex-row">
            <PackageCard
              pricing={pricing}
              subscription={subscription}
              canAct={canAct}
              onChangePackage={goToSubscribePage}
              onCancelPending={
                isPendingSub && canAct ? () => setConfirmCancelPending(true) : null
              }
            />
            <SaldoCard wallet={wallet} canAct={canAct} onTopup={() => setTopupOpen(true)} />
          </div>
        ) : null}

        {/* Statement (BR-SALDO-26). Loads independently of the cards above —
            a slow ledger page must not hold up the balance. Hidden in
            empty-state mode: there are no movements to state. */}
        {showEmptyState ? null : (
          <div className="pb-8">
            <SaldoHistory companyId={companyId} refreshKey={billingRefreshKey + ledgerKey} />
          </div>
        )}

        {/* Payment/invoice history (Everest #z94x8e0mgh). The saldo ledger above
            only records movements of spendable saldo, so a canceled or unpaid
            top-up never shows there and looked like it vanished. This lists the
            invoices themselves — every top-up / platform fee / enquiry payment,
            any status — so a cancellation is plainly accounted for. */}
        {showEmptyState ? null : (
          <div className="pb-8">
            <PaymentHistory companyId={companyId} refreshKey={billingRefreshKey + ledgerKey} />
          </div>
        )}

        {/* Whatever the caller wants under the money section — for a company
            still in verification that is the CLIK credential form, the
            rejection notice and the activity trail. */}
        {footer ? <div className="pb-8">{footer}</div> : null}
      </div>

      {/* Rupiah top-up (BR-SALDO-04). Replaces the quota-unit purchase panel on
          this page — that one buys enquiry UNITS, which is a different product.
          onSettled re-reads the wallet so a paid VA reflects without a reload. */}
      <TopupFlow
        companyId={companyId}
        wallet={wallet}
        open={topupOpen}
        resumeInvoiceId={resumeInvoiceId}
        onClose={() => {
          setTopupOpen(false)
          setResumeInvoiceId(null)
          // A cancel from the resume flow calls onClose (not onSettled), so
          // reload to clear the banner once the invoice is no longer unpaid.
          load()
        }}
        onSettled={() => {
          load()
          setLedgerKey((k) => k + 1)
        }}
      />
    </div>
  )
}

CompanyInformation.propTypes = {
  companyId: PropTypes.string,
  company: PropTypes.object,
  footer: PropTypes.node,
  // Forces the empty state and names the blocker explicitly instead of letting
  // it be inferred from a membership status the company does not have yet.
  presentation: PropTypes.shape({
    badge: BADGE_SHAPE,
    title: PropTypes.string,
    description: PropTypes.string,
    actions: PropTypes.node,
  }),
}

CompanyInformation.defaultProps = {
  companyId: null,
  company: null,
  footer: null,
  presentation: null,
}

export default CompanyInformation
